import {
  Alert,
  CreateAlertPayload,
  UpdateAlertPayload,
  AlertType,
  AlertPriority,
} from "../types/alert";
import { config } from "../config";

const API_URL = config.apiUrl;

// Create a new alert
export const createAlert = async (
  alertData: CreateAlertPayload
): Promise<Alert> => {
  try {
    const response = await fetch(`${API_URL}/api/alerts`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(alertData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to create alert");
    }

    return await response.json();
  } catch (error) {
    console.error("Error creating alert:", error);
    throw error;
  }
};

// Get alerts for a specific user
export const getUserAlerts = async (userId: string): Promise<Alert[]> => {
  try {
    const response = await fetch(`${API_URL}/api/alerts/user/${userId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to get user alerts");
    }

    return await response.json();
  } catch (error) {
    console.error("Error getting user alerts:", error);
    throw error;
  }
};

// Get alerts for a family
export const getFamilyAlerts = async (familyId: string): Promise<Alert[]> => {
  try {
    const response = await fetch(`${API_URL}/api/alerts/family/${familyId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to get family alerts");
    }

    return await response.json();
  } catch (error) {
    console.error("Error getting family alerts:", error);
    throw error;
  }
};

// Update an alert (mark as read, dismissed, etc.)
export const updateAlert = async (
  alertId: string,
  updateData: UpdateAlertPayload
): Promise<Alert> => {
  try {
    const response = await fetch(`${API_URL}/api/alerts/${alertId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(updateData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to update alert");
    }

    return await response.json();
  } catch (error) {
    console.error("Error updating alert:", error);
    throw error;
  }
};

// Utility functions for creating specific types of alerts

// Create activity-related alerts
export const createActivityAlert = async (
  familyId: string,
  userId: string,
  activityTitle: string,
  status: "completed" | "assigned" | "overdue"
): Promise<Alert> => {
  const alertData: CreateAlertPayload = {
    familyId,
    userId,
    type: AlertType.ACTIVITY,
    title: getActivityAlertTitle(status, activityTitle),
    message: getActivityAlertMessage(status, activityTitle),
    priority: status === "overdue" ? AlertPriority.HIGH : AlertPriority.MEDIUM,
    metadata: {
      activityTitle,
      activityStatus: status,
    },
  };

  return createAlert(alertData);
};

// Create achievement alerts
export const createAchievementAlert = async (
  familyId: string,
  userId: string,
  achievementType: string,
  achievementValue: number
): Promise<Alert> => {
  const alertData: CreateAlertPayload = {
    familyId,
    userId,
    type: AlertType.ACHIEVEMENT,
    title: `🏆 New Achievement Unlocked!`,
    message: getAchievementMessage(achievementType, achievementValue),
    priority: AlertPriority.MEDIUM,
    metadata: {
      achievementType,
      achievementValue,
    },
  };

  return createAlert(alertData);
};

// Create family announcement alerts
export const createFamilyAnnouncement = async (
  familyId: string,
  title: string,
  message: string,
  priority: AlertPriority = AlertPriority.MEDIUM
): Promise<Alert> => {
  const alertData: CreateAlertPayload = {
    familyId,
    // No userId means it's for the entire family
    type: AlertType.INFO,
    title,
    message,
    priority,
  };

  return createAlert(alertData);
};

// Helper functions for generating alert content
const getActivityAlertTitle = (
  status: string,
  activityTitle: string
): string => {
  switch (status) {
    case "completed":
      return `✅ Activity Completed`;
    case "assigned":
      return `📋 New Activity Assigned`;
    case "overdue":
      return `⏰ Activity Overdue`;
    default:
      return `📝 Activity Update`;
  }
};

const getActivityAlertMessage = (
  status: string,
  activityTitle: string
): string => {
  switch (status) {
    case "completed":
      return `Great job! You completed "${activityTitle}".`;
    case "assigned":
      return `You have been assigned a new activity: "${activityTitle}".`;
    case "overdue":
      return `The activity "${activityTitle}" is overdue. Please complete it as soon as possible.`;
    default:
      return `Activity "${activityTitle}" has been updated.`;
  }
};

const getAchievementMessage = (type: string, value: number): string => {
  switch (type) {
    case "points_milestone":
      return `You've reached ${value} points! Keep up the great work!`;
    case "activities_completed":
      return `Congratulations! You've completed ${value} activities!`;
    case "weekly_goal":
      return `Amazing! You've achieved your weekly goal of ${value} activities!`;
    case "streak":
      return `Fantastic! You're on a ${value}-day streak!`;
    default:
      return `You've achieved a milestone: ${type} - ${value}!`;
  }
};

// Utility function to get unread alert count
export const getUnreadAlertCount = (alerts: Alert[]): number => {
  return alerts.filter((alert) => alert.status === "UNREAD").length;
};

// Utility function to sort alerts by priority and date
export const sortAlerts = (alerts: Alert[]): Alert[] => {
  const priorityOrder = { URGENT: 4, HIGH: 3, MEDIUM: 2, LOW: 1 };

  return [...alerts].sort((a, b) => {
    // First sort by priority (higher priority first)
    const priorityDiff = priorityOrder[b.priority] - priorityOrder[a.priority];
    if (priorityDiff !== 0) return priorityDiff;

    // Then sort by date (newer first)
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });
};
